import * as THREE from "three";

const DEG = Math.PI / 180;

function getClockOffset(cs, canvasW, canvasH) {
  const hPos = cs?.clockHPos ?? 50;
  const vPos = cs?.clockVPos ?? 45;
  return {
    cx: (hPos / 100) * canvasW - canvasW / 2,
    cy: -((vPos / 100) * canvasH - canvasH / 2),
  };
}

// Android's post operations left-multiply its Matrix, so the final transform is:
// translate * camera * skew * rotateZ * scale.
// Three.js uses Y-up, which reverses X/Z rotation and both 2D skew signs.
export function applyClockTransform(mesh, cs, canvasW, canvasH) {
  const { cx, cy } = getClockOffset(cs, canvasW, canvasH);

  const scaleMat = new THREE.Matrix4().makeScale(
    cs.hStretch ?? 1,
    cs.vStretch ?? 1,
    1
  );

  const rotZ = -((cs.clockRotation || 0) + (cs.perspRotZ || 0)) * DEG;
  const rotZMat = new THREE.Matrix4().makeRotationZ(rotZ);

  const skX = -Math.tan((cs.hSkew || 0) * 45 * DEG);
  const skY = -Math.tan((cs.vSkew || 0) * 45 * DEG);
  const skewMat = new THREE.Matrix4().set(
    1,   skX, 0, 0,
    skY, 1,   0, 0,
    0,   0,   1, 0,
    0,   0,   0, 1
  );

  // Android Camera applies rotateX followed by rotateY. Converting canvas Y
  // (down) to Three.js Y (up) preserves X's value sign and reverses Y's axis.
  const cameraRotationMat = new THREE.Matrix4()
    .makeRotationX((cs.perspRotX || 0) * DEG)
    .multiply(
      new THREE.Matrix4().makeRotationY((cs.perspRotY || 0) * DEG)
    );

  const matrix = new THREE.Matrix4()
    .makeTranslation(cx, cy, 0)
    .multiply(cameraRotationMat)
    .multiply(skewMat)
    .multiply(rotZMat)
    .multiply(scaleMat);

  mesh.matrixAutoUpdate = false;
  mesh.matrix.copy(matrix);
}

// Skew is included in applyClockTransform. This remains for existing callers.
export function applySkewToMatrix() {}

// Android Camera receives inches at 72 dpi:
// -(500 * canvasH / (2400 * 72)) inches = -(500 * canvasH / 2400) pixels.
export function makePerspectiveCamera(canvasW, canvasH) {
  const distance = (500 * canvasH) / 2400;
  const fov = 2 * Math.atan(canvasH / 2 / distance) * (180 / Math.PI);
  const camera = new THREE.PerspectiveCamera(
    fov,
    canvasW / canvasH,
    1,
    10000
  );
  updatePerspectiveCamera(camera, null, canvasW, canvasH);
  return camera;
}

// Android applies its Camera matrix before the final clock translation. Moving
// the Three.js camera and its principal point with the clock reproduces that
// local projection while leaving every z=0 image layer pixel-for-pixel stable.
export function updatePerspectiveCamera(camera, cs, canvasW, canvasH) {
  const { cx, cy } = getClockOffset(cs, canvasW, canvasH);
  const distance = (500 * canvasH) / 2400;

  camera.position.set(cx, cy, distance);
  camera.setViewOffset(canvasW, canvasH, -cx, cy, canvasW, canvasH);
  camera.updateMatrixWorld(true);
}
